// A LIST's totals row (PAT-10), in the list grid's footer above the pager —
// the figures under their columns, the label in the columns to their left
// (the shared AlignedTotalsRow; BP-91 is the one-figure detail-tab twin).
//
// First consumer: Flights › List (Mustang 2026-10-16, "add the total of flight
// time at the bottom of the column. This is what we have in AirOps"). The list
// is server-paged, so the figures are the SERVER's, over everything the list's
// filters select — never a sum of the page on screen. The caller passes them
// to AeronetDataGrid's `columnTotals`; null hides the row (nothing loaded yet,
// or a read that failed: no row rather than a 0.00 that reads as measured).
//
// Pro (v6) only: it reads @mui/x-data-grid-pro's context. A Premium grid uses
// ColumnAlignedTotal.
import { useState, type ReactNode } from "react";
import { Box } from "@mui/material";
import {
  GridFooter,
  gridColumnPositionsSelector,
  gridVisibleColumnDefinitionsSelector,
  useGridApiContext,
  useGridApiEventHandler,
  useGridSelector,
} from "@mui/x-data-grid-pro";

import { AlignedTotalsRow } from "./AlignedTotalsRow";
import { LISTING_GUTTER } from "../../utils/dataGridSx";

export interface ColumnTotals {
  label: ReactNode;
  /** grid field → its figure. */
  values: Readonly<Record<string, ReactNode>>;
}

export function ListColumnTotals({ label, values }: ColumnTotals) {
  const apiRef = useGridApiContext();
  const columns = useGridSelector(apiRef, gridVisibleColumnDefinitionsSelector);
  const positions = useGridSelector(apiRef, gridColumnPositionsSelector);
  const [scrollLeft, setScrollLeft] = useState(0);
  useGridApiEventHandler(apiRef, "scrollPositionChange", (p) => setScrollLeft(p.left));
  return (
    <AlignedTotalsRow
      columns={columns}
      positions={positions}
      scrollLeft={scrollLeft}
      label={label}
      values={values}
      labelTestId="list-totals-label"
      valueTestId={(field) => `list-total-${field}`}
      fallbackTestId="list-totals-fallback"
    />
  );
}

/** The list grid's footer: the totals row (when given), then MUI's own footer
 *  (pager, selected count) unchanged. Set as `slots.footer`; the totals arrive
 *  through `slotProps.footer`, so the slot's identity never changes. */
export function ListFooterWithTotals({ columnTotals }: { columnTotals?: ColumnTotals | null }) {
  return (
    <>
      {columnTotals ? (
        // The list body's own gutter (DataGrid.tsx `.MuiDataGrid-main`), so
        // the column offsets measured from the body land on the same pixels.
        <Box sx={{ mx: LISTING_GUTTER }} data-testid="list-totals-row">
          <ListColumnTotals {...columnTotals} />
        </Box>
      ) : null}
      <GridFooter />
    </>
  );
}

export default ListColumnTotals;
